/**
 * WebSocket Utility Module
 * 
 * Provides centralized WebSocket management for the new namespaced event structure.
 * Handles connection management, event registration, and error handling.
 */

import { io, Socket } from 'socket.io-client';

/**
 * WebSocket event handlers type
 */
type EventHandler = (data: any) => void;
type EventHandlers = Record<string, EventHandler[]>;

/**
 * WebSocket connection configuration
 */
interface WebSocketConfig {
  url?: string;
  namespace?: string;
  reconnection?: boolean;
  reconnectionDelay?: number;
  reconnectionDelayMax?: number;
  reconnectionAttempts?: number;
  timeout?: number;
}

/**
 * WebSocket client class for managing connections and events
 */
class WebSocketClient {
  private socket: Socket | null = null;
  private config: WebSocketConfig;
  private eventHandlers: EventHandlers = {};
  private isConnected = false;
  private reconnectAttempts = 0;

  constructor(config: WebSocketConfig = {}) {
    this.config = {
      url: config.url || window.location.origin,
      namespace: config.namespace || '/',
      reconnection: config.reconnection ?? true,
      reconnectionDelay: config.reconnectionDelay || 1000,
      reconnectionDelayMax: config.reconnectionDelayMax || 5000,
      reconnectionAttempts: config.reconnectionAttempts || 5,
      timeout: config.timeout || 20000,
      ...config
    };
  }

  /**
   * Connect to WebSocket server
   */
  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const socketUrl = this.config.namespace === '/' 
          ? this.config.url 
          : `${this.config.url}${this.config.namespace}`;

        this.socket = io(socketUrl, {
          reconnection: this.config.reconnection,
          reconnectionDelay: this.config.reconnectionDelay,
          reconnectionDelayMax: this.config.reconnectionDelayMax,
          reconnectionAttempts: this.config.reconnectionAttempts,
          timeout: this.config.timeout
        });

        // Set up connection event handlers
        this.socket.on('connect', () => {
          console.log(`✅ Connected to WebSocket namespace: ${this.config.namespace}`);
          this.isConnected = true;
          this.reconnectAttempts = 0;
          
          // Register all pending event handlers that were added before connection
          Object.entries(this.eventHandlers).forEach(([eventName, handlers]) => {
            handlers.forEach(handler => {
              this.socket!.on(eventName, handler);
            });
          });
          
          this.emit('connection_established', { namespace: this.config.namespace });
          resolve();
        });

        this.socket.on('disconnect', (reason) => {
          console.log(`❌ Disconnected from WebSocket namespace: ${this.config.namespace}, reason: ${reason}`);
          this.isConnected = false;
          this.emit('connection_lost', { namespace: this.config.namespace, reason });
        });

        this.socket.on('connect_error', (error) => {
          console.error(`❌ Connection error for namespace ${this.config.namespace}:`, error);
          this.reconnectAttempts++;
          this.emit('connection_error', { 
            namespace: this.config.namespace, 
            error: error.message,
            attempts: this.reconnectAttempts
          });
          
          if (this.reconnectAttempts >= (this.config.reconnectionAttempts || 5)) {
            reject(new Error(`Failed to connect after ${this.reconnectAttempts} attempts: ${error.message}`));
          }
        });

        this.socket.on('reconnect', (attemptNumber) => {
          console.log(`✅ Reconnected to WebSocket namespace: ${this.config.namespace} after ${attemptNumber} attempts`);
          this.emit('reconnected', { namespace: this.config.namespace, attempts: attemptNumber });
        });

        this.socket.on('reconnect_error', (error) => {
          console.error(`❌ Reconnection error for namespace ${this.config.namespace}:`, error);
          this.emit('reconnection_error', { namespace: this.config.namespace, error: error.message });
        });

        // Set up error handling for API responses
        this.socket.on('error', (error) => {
          console.error(`❌ Socket error in namespace ${this.config.namespace}:`, error);
          this.emit('socket_error', { namespace: this.config.namespace, error });
        });

        // Handle success responses
        this.socket.onAny((eventName, data) => {
          if (eventName.endsWith('_success')) {
            console.log(`✅ Success event: ${eventName}`, data);
          } else if (eventName.endsWith('_error')) {
            console.error(`❌ Error event: ${eventName}`, data);
          }
        });

      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      console.log(`🔌 Disconnected from WebSocket namespace: ${this.config.namespace}`);
    }
  }

  /**
   * Check if connected
   */
  connected(): boolean {
    return this.isConnected && this.socket?.connected === true;
  }

  /**
   * Emit an event to the server
   */
  send(eventName: string, data?: any): void {
    if (this.socket && this.isConnected) {
      this.socket.emit(eventName, data);
      console.log(`📤 Sent event: ${eventName}`, data);
    } else {
      console.warn(`⚠️ Cannot send event ${eventName}: not connected to namespace ${this.config.namespace}`);
    }
  }

  /**
   * Register an event handler
   */
  on(eventName: string, handler: EventHandler): void {
    if (!this.eventHandlers[eventName]) {
      this.eventHandlers[eventName] = [];
    }
    this.eventHandlers[eventName].push(handler);

    // Register with socket if connected
    if (this.socket) {
      this.socket.on(eventName, handler);
    }
  }

  /**
   * Remove an event handler
   */
  off(eventName: string, handler?: EventHandler): void {
    if (handler) {
      const handlers = this.eventHandlers[eventName];
      if (handlers) {
        const index = handlers.indexOf(handler);
        if (index > -1) {
          handlers.splice(index, 1);
        }
      }
      if (this.socket) {
        this.socket.off(eventName, handler);
      }
    } else {
      // Remove all handlers for this event
      delete this.eventHandlers[eventName];
      if (this.socket) {
        this.socket.off(eventName);
      }
    }
  }

  /**
   * Emit event to local handlers (for internal events)
   */
  private emit(eventName: string, data?: any): void {
    const handlers = this.eventHandlers[eventName];
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in event handler for ${eventName}:`, error);
        }
      });
    }
  }

  /**
   * Get connection status information
   */
  getStatus(): { connected: boolean; namespace: string; attempts: number } {
    return {
      connected: this.isConnected,
      namespace: this.config.namespace || '/',
      attempts: this.reconnectAttempts
    };
  }
}

/**
 * Dispatcher WebSocket client for dashboard operations
 */
export class DispatcherWebSocket extends WebSocketClient {
  constructor(config: Omit<WebSocketConfig, 'namespace'> = {}) {
    // Use VITE_WS_URL in production, window.location.origin for development (uses Vite proxy)
    const wsUrl = import.meta.env.VITE_WS_URL || window.location.origin;
    super({ 
      url: wsUrl,
      ...config, 
      namespace: '/dispatcher' 
    });
  }

  /**
   * Register as a dispatcher
   */
  registerDispatcher(dispatcherId: string, dispatcherName: string): void {
    this.send('register_dispatcher', {
      dispatcher_id: dispatcherId,
      dispatcher_name: dispatcherName
    });
  }

  /**
   * Request all drivers data
   */
  requestAllDrivers(zona?: string): void {
    this.send('request_all_drivers', zona ? { zona } : {});
  }

  /**
   * Request specific driver details
   */
  requestDriverDetails(driverId: string): void {
    this.send('request_driver_details', { driver_id: driverId });
  }

  /**
   * Request delivery statistics
   */
  requestDeliveryStats(zona?: string, dateRange?: string): void {
    this.send('request_delivery_stats', { zona, date_range: dateRange });
  }

  /**
   * Request zone statistics
   */
  requestZoneStats(): void {
    this.send('request_zone_stats', {});
  }

  /**
   * Send message to driver
   */
  sendDriverMessage(driverId: string, messageType: string, message: string): void {
    this.send('send_driver_message', {
      driver_id: driverId,
      message_type: messageType,
      message: message
    });
  }

  /**
   * Reassign delivery
   */
  reassignDelivery(deliveryId: string, newDriverId: string, reason?: string): void {
    this.send('reassign_delivery', {
      delivery_id: deliveryId,
      new_driver_id: newDriverId,
      reason: reason || 'Reassigned by dispatcher'
    });
  }

  /**
   * Request system health
   */
  requestSystemHealth(): void {
    this.send('request_system_health', {});
  }
}

/**
 * Driver WebSocket client for driver operations
 */
export class DriverWebSocket extends WebSocketClient {
  constructor(config: Omit<WebSocketConfig, 'namespace'> = {}) {
    // Use VITE_WS_URL in production, window.location.origin for development (uses Vite proxy)
    const wsUrl = import.meta.env.VITE_WS_URL || window.location.origin;
    super({ 
      url: wsUrl,
      ...config, 
      namespace: '/driver' 
    });
  }

  /**
   * Register as a driver
   */
  registerDriver(driverId: string, driverName: string): void {
    this.send('register_driver', {
      driver_id: driverId,
      driver_name: driverName
    });
  }

  /**
   * Update driver location
   */
  updateLocation(lat: number, lon: number, accuracy?: number): void {
    this.send('update_location', {
      lat: lat,
      lon: lon,
      accuracy: accuracy || 0
    });
  }

  /**
   * Update delivery status
   */
  updateDeliveryStatus(deliveryId: string, status: string, options: {
    notes?: string;
    failureReason?: string;
    photoPath?: string;
  } = {}): void {
    this.send('delivery_status_update', {
      delivery_id: deliveryId,
      status: status,
      notes: options.notes,
      failure_reason: options.failureReason,
      photo_path: options.photoPath
    });
  }

  /**
   * Request route status
   */
  requestRouteStatus(): void {
    this.send('request_route_status', {});
  }

  /**
   * Request next delivery
   */
  requestNextDelivery(): void {
    this.send('request_next_delivery', {});
  }

  /**
   * Send heartbeat
   */
  sendHeartbeat(status?: any): void {
    this.send('heartbeat', status || {});
  }
}

// Export the base class and specialized clients
export { WebSocketClient };
export default WebSocketClient;